import {
  ApostleClassId,
  ApostleClassType,
  ApostleSkill,
  ApostleSkillExp,
  ApostleSkillSpecialId,
  ApostleSkillType
} from '@apeiron/apostle'
import { createStrictContext, LOCAL_LIST_SIZE, MayNull } from '@apeiron/temp'
import { SkillProvider } from '@src/contexts/skill/SkillContext'
import useFetchApostleSkillJson from '@src/hooks/json/useFetchApostleSkillJson'
import * as R from 'ramda'
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { zeroPad } from 'react-countdown'

const [ContextProvider, useApostleSkillsContext] =
  createStrictContext<ContextValue>('ApostleSkills')

export { useApostleSkillsContext }

export const ApostleSkillsProvider: FC<Props> = (props: Props) => {
  const { children, defaultDetail = false } = props

  const { data } = useFetchApostleSkillJson()

  const [allSkills, setAllSkills] = useState<ApostleSkill[]>([])

  const sortSkills = useCallback((skills: ApostleSkill[]) => {
    const sortByType = (a: ApostleSkill, b: ApostleSkill) => {
      switch (a.type) {
        case ApostleSkillType.AutoAttack:
          return -1

        case ApostleSkillType.Innate:
          return b.type === ApostleSkillType.AutoAttack ? 1 : -1

        case ApostleSkillType.ActiveSkill:
          return 1
      }

      return 0
    }

    const sort = R.sortWith<ApostleSkill>([
      sortByType,
      R.ascend(R.propOr('', 'name')),
      R.descend(R.propOr(0, 'level'))
    ])

    return sort(skills)
  }, [])

  const pageSkills = useCallback((skills: ApostleSkill[]) => {
    return R.take(LOCAL_LIST_SIZE, skills)
  }, [])

  const filterNow = useCallback(
    (params: FilterParams): ApostleSkill[] => {
      const { name, types, classes } = params

      let result = allSkills

      if (R.isNotNil(name)) {
        result = R.filter(skill => R.includes(name, skill.name || ''), result)
      }

      if (R.isNotNil(types) && !R.isEmpty(types)) {
        result = R.filter(skill => R.includes(skill.type, types), result)
      }

      if (R.isNotNil(classes) && !R.isEmpty(classes)) {
        result = R.filter(skill => R.includes(skill.class, classes), result)
      }

      return pageSkills(result)
    },
    [allSkills, pageSkills]
  )

  const searchNow = useCallback(
    (params: SearchParams): ApostleSkill[] => {
      const { classId, keys, name, types, classes } = params

      let energizedSkills: ApostleSkill[] = []

      if (R.isNotNil(classId)) {
        energizedSkills = R.filter(skill => {
          const key = `Skill_DH${zeroPad(classId, 3)}_${
            ApostleSkillSpecialId.Innate
          }`

          return skill.key === key
        }, allSkills)
      }

      let autoAttackSkills: ApostleSkill[] = []

      if (R.isNotNil(classId)) {
        autoAttackSkills = R.filter(skill => {
          const key = `Skill_DH${zeroPad(classId, 3)}_${
            ApostleSkillSpecialId.AutoAttack
          }`

          return skill.key === key
        }, allSkills)
      }

      let cardSkills: ApostleSkill[] = []

      if (R.isNotNil(keys) && !R.isEmpty(keys)) {
        cardSkills = R.filter(skill => R.includes(skill.key, keys), allSkills)
      }

      let result = [...autoAttackSkills, ...energizedSkills, ...cardSkills]

      if (R.isNotNil(name)) {
        result = R.filter(skill => R.includes(name, skill.name || ''), result)
      }

      if (R.isNotNil(types) && !R.isEmpty(types)) {
        result = R.filter(skill => R.includes(skill.type, types), result)
      }

      if (R.isNotNil(classes) && !R.isEmpty(classes)) {
        result = R.filter(skill => R.includes(skill.class, classes), result)
      }

      return pageSkills(result)
    },
    [allSkills, pageSkills]
  )

  const injectLevel = useCallback(
    (skill: MayNull<ApostleSkill>, level: number): MayNull<ApostleSkill> => {
      return R.isNil(skill) ? null : R.assoc('level', level, skill)
    },
    []
  )

  const filterSkills = useCallback(
    (params: FilterParams): ApostleSkill[] => {
      return filterNow(params)
    },
    [filterNow]
  )

  const getSkill = useCallback(
    (key: string, level: number): MayNull<ApostleSkill> => {
      const skill = R.find(R.propEq(key, 'key'), allSkills)

      return injectLevel(skill, level)
    },
    [allSkills, injectLevel]
  )

  const searchSkills = useCallback(
    (params: SearchParams): ApostleSkill[] => {
      const { skillExps, ...rest } = params

      const keys = R.isNotNil(skillExps)
        ? {
            keys: R.pluck('key', skillExps)
          }
        : {}

      const injectLevel = R.map((skill: ApostleSkill) => {
        const matchedExp = R.find(exp => skill.key === exp.key, skillExps || [])

        return matchedExp ? R.assoc('level', matchedExp.level, skill) : skill
      })

      const skills = searchNow({ ...rest, ...keys })

      return sortSkills(injectLevel(skills))
    },
    [searchNow, sortSkills]
  )

  useEffect(() => {
    if (R.isNotNil(data)) {
      setAllSkills(sortSkills(data))
    }
  }, [data])

  const apostleSkillsContextValue = useMemo<ContextValue>(() => {
    return {
      allSkills,
      filterSkills,
      getSkill,
      searchSkills
    }
  }, [allSkills, filterSkills, getSkill, searchSkills])

  return (
    <SkillProvider defaultDetail={defaultDetail}>
      <ContextProvider value={apostleSkillsContextValue}>
        {children}
      </ContextProvider>
    </SkillProvider>
  )
}

type FilterParams = {
  name?: MayNull<string>
  types?: MayNull<ApostleSkillType[]>
  classes?: MayNull<ApostleClassType[]>
}

type SearchParams = FilterParams & {
  classId?: MayNull<ApostleClassId>
  keys?: MayNull<string[]>
  skillExps?: MayNull<ApostleSkillExp[]>
}

type ContextValue = {
  allSkills: ApostleSkill[]
  filterSkills: (params: FilterParams) => ApostleSkill[]
  getSkill: (id: string, level: number) => MayNull<ApostleSkill>
  searchSkills: (params: SearchParams) => ApostleSkill[]
}

type Props = {
  children?: ReactNode
  defaultDetail?: boolean
}
